<template>
  <span>
    <slot name="button" v-bind="{ onclick: toggle }">
      <b-button variant="primary" class="mr-1" size="xs" @click="toggle"
        ><i class="fas fa-edit"></i>&nbsp;Edit</b-button
      >
    </slot>

    <b-modal
      @show="resetModal"
      @hidden="resetModal"
      ref="modal-container"
      v-model="modalShow"
      size="lg"
      :title="title"
      :static="true"
      :lazy="true"
      hide-footer
    >
      <form ref="form" @submit.stop.prevent="handleSubmit">
        <b-form-group
          :label="type !== 'checkbox' ? label : null"
          :label-for="`${id}-input`"
          v-for="{
            id,
            label,
            validate = {},
            closeOnSelect,
            required,
            type,
            disabled = false,
            options = null,
            placeholder = null
          } in inputs"
          :key="id"
        >
          <b-form-input
            v-if="type === 'string'"
            :id="`${id}-input`"
            v-model="formValues[id]"
            v-validate="validate"
            :placeholder="placeholder"
            :state="validateState(id)"
            :aria-describedby="`${id}-live-feedback`"
            :data-vv-name="id"
            :data-vv-as="label"
            autocomplete="off"
            :disabled="disabled"
          />
          <b-form-input
            v-if="type === 'number'"
            type="number"
            :id="`${id}-input`"
            v-model="formValues[id]"
            v-validate="validate"
            :state="validateState(id)"
            :aria-describedby="`${id}-live-feedback`"
            :data-vv-name="id"
            :data-vv-as="label"
            autocomplete="off"
            :disabled="disabled"
          />
          <b-form-select
            v-if="type === 'select'"
            :id="`${id}-input`"
            v-model="formValues[id]"
            v-validate="validate"
            :options="
              options.map(o => {
                return { value: o, text: o ? o : '' }
              })
            "
            :state="validateState(id)"
            :aria-describedby="`${id}-live-feedback`"
            :data-vv-name="id"
            :data-vv-as="label"
            autocomplete="off"
            :disabled="disabled"
          />

          <b-form-checkbox
            v-if="type === 'checkbox'"
            v-model="formValues[id]"
            :value="true"
            :unchecked-value="false"
          >
            {{ label }}
          </b-form-checkbox>
          <b-form-invalid-feedback :id="`${id}-live-feedback`">{{
            veeErrors.first(id)
          }}</b-form-invalid-feedback>
        </b-form-group>
      </form>

      <b-button class="mt-2 float-right" variant="primary" @click="handleSubmit"
        >Save</b-button
      >
    </b-modal>
  </span>
</template>

<script>
import { getRouteIcon, ROUTE_AGGR_CONFIGS } from '../../utils/routerIcons'
import VJsoneditor from 'v-jsoneditor/src/index'
import Vue from 'vue'
import { ProvidersIds, EGameCategories } from '../../definitions/ProviderID'

export default {
  data() {
    return {
      modalShow: false,
      formValues: this.getInitialValues(),
      valueEditorError: ''
    }
  },
  components: {
    VJsoneditor
  },
  props: {
    create: Boolean,
    title: String,
    defaultValues: Object
  },
  methods: {
    getInitialValues() {
      return {
        id: null,
        gameId: null,
        tableId: null,
        type: null,
        hidden: false,
        opened: true,
        name: '',
        fullName: '',
        providerId: null,
        developer: null,
        category: null,
        jackpot: false,
        freespins: false,
        demo: false,
        rtp: null
      }
    },
    setDefaults() {
      let initialValues = this.getInitialValues()

      for (let field in initialValues) {
        Vue.set(this.formValues, field, initialValues[field])
      }

      if (this.defaultValues) {
        for (let field in this.defaultValues) {
          Vue.set(this.formValues, field, this.defaultValues[field])
        }
      }
    },
    validateState(ref) {
      if (
        this.veeFields[ref] &&
        (this.veeFields[ref].dirty || this.veeFields[ref].validated)
      ) {
        return !this.veeErrors.has(ref)
      }
      return null
    },
    resetModal() {
      this.setDefaults()
    },
    hideModal() {
      this.$refs['modal-container'].hide()
    },
    toggle() {
      this.modalShow = !this.modalShow
    },
    handleSubmit() {
      this.$validator.validateAll().then(result => {
        if (!result) {
          return
        }

        this.$http({
          url: this.create
            ? '/api/v1/provider-games'
            : '/api/v1/provider-games/' + this.formValues.id,
          data: {
            ...this.formValues,
            gameId: String(this.formValues.gameId),
            tableId: Number.parseInt(this.formValues.tableId),
            type: Number.parseInt(this.formValues.type)
          },
          method: this.create ? 'POST' : 'PUT',
          headers: {
            'Content-Type': 'application/json'
          }
        })
          .then(() => {
            setTimeout(() => {
              this.$emit('finish')
            }, 500)

            this.hideModal()
            this.$toastr.i('Success')
          })
          .catch(err => {
            this.$toastr.e(JSON.stringify(err.response.data.message))

            throw err
          })
      })
    }
  },
  computed: {
    inputs() {
      let inputs = [
        {
          id: 'providerId',
          label: 'Provider',
          validate: { required: true },
          type: 'select',
          disabled: !this.create,
          options: ProvidersIds
        },
        {
          id: 'category',
          label: 'Category',
          validate: { required: false },
          type: 'select',
          options: [null, ...EGameCategories]
        },
        {
          id: 'gameId',
          label: 'Game ID',
          validate: { required: true },
          type: 'string'
        },
        {
          id: 'developer',
          label: 'Developer',
          validate: { required: false },
          type: 'string'
        },
        {
          id: 'tableId',
          label: 'Table ID',
          validate: { required: true },
          type: 'number'
        },
        {
          id: 'type',
          label: 'Type',
          validate: { required: false },
          type: 'number'
        },
        {
          id: 'hidden',
          label: 'Hidden',
          validate: { required: true },
          type: 'checkbox'
        },
        {
          id: 'opened',
          label: 'Opened',
          validate: { required: true },
          type: 'checkbox'
        },
        {
          id: 'name',
          label: 'Name',
          validate: { required: false },
          type: 'string'
        },
        {
          id: 'fullName',
          label: 'Full Name',
          validate: { required: false },
          type: 'string'
        },
        {
          id: 'jackpot',
          label: 'Jackpot',
          validate: { required: false },
          type: 'checkbox'
        },
        {
          id: 'freespins',
          label: 'Freespins',
          validate: { required: false },
          type: 'checkbox'
        },
        {
          id: 'demo',
          label: 'Demo',
          validate: { required: false },
          type: 'checkbox'
        },
        {
          id: 'rtp',
          label: 'RTP',
          validate: { required: false },
          type: 'string'
        }
      ]

      if (this.create) {
        return inputs
      }

      return [
        {
          id: 'id',
          label: '_ID',
          validate: { required: true },
          type: 'string',
          disabled: true
        },
        ...inputs
      ]
    },
    routeIcon: () => getRouteIcon(ROUTE_AGGR_CONFIGS)
  },
  mounted() {
    this.setDefaults()
  }
}
</script>
